import React from 'react';
import { JobSource, FormErrors, ContractType, Prefecture, Occupation, JobSourceFeature, Skill, Industry } from '../types';
import RelationSelector from '../fields/RelationSelector';
import RelationSelectorWithPriority from '../fields/RelationSelectorWithPriority';

interface RelationsSectionProps {
  formData: JobSource;
  setFormData: React.Dispatch<React.SetStateAction<JobSource>>;
  contractTypes?: ContractType[];
  prefectures?: Prefecture[];
  occupations?: Occupation[];
  job_source_features?: JobSourceFeature[];
  skills?: Skill[];
  industries?: Industry[];
  errors: FormErrors;
}

const RelationsSection: React.FC<RelationsSectionProps> = ({
  formData,
  setFormData,
  contractTypes = [],
  prefectures = [],
  occupations = [],
  job_source_features = [],
  skills = [],
  industries = [],
  errors,
}) => {

  const handleContractTypeChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, contract_type_ids: ids }));
  };

  const handlePrefectureChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, prefecture_ids: ids }));
  };

  const handleOccupationChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, occupation_ids: ids }));
  };

  const handleJobSourceFeatureChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, job_source_feature_ids: ids }));
  };

  const handleSkillChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, skill_ids: ids }));
  };

  const handleSkillPriorityChange = (id: number, priority: number) => {
    setFormData((prev) => ({
      ...prev,
      skill_priorities: {
        ...(prev.skill_priorities || {}),
        [id]: priority,
      },
    }));
  };

  const handleIndustryChange = (ids: number[]) => {
    setFormData((prev) => ({ ...prev, industry_ids: ids }));
  };

  const handleIndustryPriorityChange = (id: number, priority: number) => {
    setFormData((prev) => ({
      ...prev,
      industry_priorities: {
        ...(prev.industry_priorities || {}),
        [id]: priority,
      },
    }));
  };

  const handleOccupationPriorityChange = (id: number, priority: number) => {
    setFormData((prev) => ({
      ...prev,
      occupation_priorities: {
        ...(prev.occupation_priorities || {}),
        [id]: priority,
      },
    }));
  };

  // 都道府県を地域ごとにグループ化
  const prefecturesByRegion = (prefectures || []).reduce((acc, prefecture) => {
    const regionName = prefecture.region?.name || '未分類';
    if (!acc[regionName]) {
      acc[regionName] = [];
    }
    acc[regionName].push(prefecture);
    return acc;
  }, {} as Record<string, Prefecture[]>);

  // 地域ごとの都道府県選択ハンドラー
  const handleRegionPrefectureChange = (regionPrefectures: Prefecture[], ids: number[]) => {
    // 他の地域の選択を保持しつつ、この地域の選択を更新
    const otherRegionIds = (formData.prefecture_ids || []).filter(
      (id) => !regionPrefectures.some((p) => p.id === id)
    );
    handlePrefectureChange([...otherRegionIds, ...ids]);
  };

  return (
    <div className="tab-pane active">
      <div className="form-group mb-4">
        <RelationSelector
          label="対応契約形態"
          selectedIds={formData.contract_type_ids || []}
          options={(contractTypes || []).map((ct) => ({ id: ct.id, name: ct.name }))}
          onChange={handleContractTypeChange}
          errors={errors.contract_type_ids}
          columns={3}
          showSelectAll={true}
        />
        <small className="form-text text-muted">このエージェントが対応できる契約形態を選択してください</small>
      </div>

      <div className="form-group mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label fw-semibold mb-0">対応都道府県</label>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => handlePrefectureChange((prefectures || []).map((p) => p.id))}
            >
              全ての都道府県を選択
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => handlePrefectureChange([])}
              disabled={(formData.prefecture_ids || []).length === 0}
            >
              全て解除
            </button>
            <span className="text-muted small align-self-center">
              {(formData.prefecture_ids || []).length}/{(prefectures || []).length}件選択中
            </span>
          </div>
        </div>
        {Object.entries(prefecturesByRegion || {}).map(([regionName, regionPrefectures]) => (
          <div key={regionName} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="fw-semibold mb-0">{regionName}</h6>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => {
                    const regionIds = regionPrefectures.map((p) => p.id);
                    handleRegionPrefectureChange(regionPrefectures, regionIds);
                  }}
                >
                  この地域を全て選択
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => handleRegionPrefectureChange(regionPrefectures, [])}
                  disabled={
                    !(formData.prefecture_ids || []).some((id) =>
                      regionPrefectures.some((p) => p.id === id)
                    )
                  }
                >
                  この地域を全て解除
                </button>
              </div>
            </div>
            <RelationSelector
              label=""
              selectedIds={formData.prefecture_ids || []}
              options={regionPrefectures.map((p) => ({ id: p.id, name: p.name }))}
              onChange={(ids) => handleRegionPrefectureChange(regionPrefectures, ids)}
              errors={errors.prefecture_ids}
              columns={4}
              showSelectAll={false}
            />
          </div>
        ))}
        <small className="form-text text-muted">このエージェントが対応できる都道府県を選択してください</small>
      </div>

      <div className="form-group mb-4">
        <RelationSelectorWithPriority
          label="対応スキル"
          selectedIds={formData.skill_ids || []}
          options={(skills || []).map((s) => ({ id: s.id, name: s.name }))}
          priorities={formData.skill_priorities || {}}
          onChange={handleSkillChange}
          onPriorityChange={handleSkillPriorityChange}
          errors={errors.skill_ids}
          columns={3}
          showSelectAll={true}
        />
        <small className="form-text text-muted">このエージェントが対応できるスキルを選択し、表示優先順位を設定してください</small>
      </div>

      <div className="form-group mb-4">
        <RelationSelectorWithPriority
          label="対応業界"
          selectedIds={formData.industry_ids || []}
          options={(industries || []).map((i) => ({ id: i.id, name: i.name }))}
          priorities={formData.industry_priorities || {}}
          onChange={handleIndustryChange}
          onPriorityChange={handleIndustryPriorityChange}
          errors={errors.industry_ids}
          columns={3}
          showSelectAll={true}
        />
        <small className="form-text text-muted">このエージェントが対応できる業界を選択し、表示優先順位を設定してください</small>
      </div>

      <div className="form-group mb-4">
        <RelationSelectorWithPriority
          label="対応職種"
          selectedIds={formData.occupation_ids || []}
          options={(occupations || []).map((occ) => ({ id: occ.id, name: occ.name }))}
          priorities={formData.occupation_priorities || {}}
          onChange={handleOccupationChange}
          onPriorityChange={handleOccupationPriorityChange}
          errors={errors.occupation_ids}
          columns={3}
          showSelectAll={true}
        />
        <small className="form-text text-muted">このエージェントが対応できる職種を選択し、表示優先順位を設定してください</small>
      </div>

      <div className="form-group mb-4">
        <RelationSelector
          label="特徴"
          selectedIds={formData.job_source_feature_ids || []}
          options={(job_source_features || []).map((f) => ({ id: f.id, name: f.name }))}
          onChange={handleJobSourceFeatureChange}
          errors={errors.job_source_feature_ids}
          columns={3}
          showSelectAll={true}
        />
        <small className="form-text text-muted">このエージェントの特徴を選択してください</small>
      </div>
    </div>
  );
};

export default RelationsSection;

